import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { load } from "@cashfreepayments/cashfree-js";
import { Check, Crown } from "@phosphor-icons/react";
import { toast } from "sonner";
import { API, BACKEND_URL, fmtErr, inr } from "../lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function Billing() {
  const [plans, setPlans] = useState([]);
  const [sub, setSub] = useState(null);
  const [config, setConfig] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [period, setPeriod] = useState("monthly");
  const [checkoutPlan, setCheckoutPlan] = useState(null);
  const [phone, setPhone] = useState("");
  const [licenseKey, setLicenseKey] = useState("");
  const [licenseBusy, setLicenseBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [params] = useSearchParams();
  const planParam = params.get("plan");

  useEffect(() => {
    if (planParam && plans.length && config?.payments_enabled && !checkoutPlan) {
      const target = plans.find((p) => p.key === planParam && p.price_monthly > 0);
      if (target) setCheckoutPlan(target);
    }
  }, [planParam, plans, config, checkoutPlan]);

  const loadData = async () => {
    try {
      const [p, s, c, inv] = await Promise.all([
        API.get("/billing/plans"),
        API.get("/billing/my-subscription"),
        API.get("/billing/config"),
        API.get("/billing/invoices"),
      ]);
      setPlans(p.data);
      setSub(s.data);
      setConfig(c.data);
      setInvoices(inv.data);
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const subscribe = async () => {
    if (config?.gateway === "cashfree" && !/^[6-9][0-9]{9}$/.test(phone)) {
      return toast.error("Enter a valid 10-digit Indian mobile number");
    }
    setBusy(true);
    try {
      const r = await API.post("/billing/checkout", {
        plan_key: checkoutPlan.key,
        period,
        customer_phone: phone,
      });
      if (r.data.gateway === "razorpay") {
        const scriptReady = await new Promise((resolve) => {
          if (window.Razorpay) return resolve(true);
          const script = document.createElement("script");
          script.src = "https://checkout.razorpay.com/v1/checkout.js";
          script.onload = () => resolve(true);
          script.onerror = () => resolve(false);
          document.body.appendChild(script);
        });
        if (!scriptReady || !window.Razorpay) {
          throw new Error("Could not load Razorpay checkout. Please try again.");
        }
        const checkout = new window.Razorpay({
          key: r.data.razorpay_key_id,
          subscription_id: r.data.razorpay_subscription_id,
          name: "ADS Billing",
          description: `${checkoutPlan.name} ${period} subscription`,
          prefill: { name: sub?.user?.name, email: sub?.user?.email },
          theme: { color: "#059669" },
          handler: async (response) => {
            try {
              const verified = await API.post("/billing/razorpay/verify", {
                subscription_id: response.razorpay_subscription_id,
                payment_id: response.razorpay_payment_id,
                signature: response.razorpay_signature,
              });
              if (verified.data.access_active) {
                toast.success("Subscription activated");
                setCheckoutPlan(null);
                setBusy(false);
                await loadData();
              } else {
                window.location.assign(`/billing/return?subscription_id=${encodeURIComponent(response.razorpay_subscription_id)}`);
              }
            } catch (error) {
              toast.error(fmtErr(error));
              setBusy(false);
            }
          },
          modal: {
            ondismiss: () => setBusy(false),
          },
        });
        checkout.open();
      } else {
        const cashfree = await load({ mode: r.data.cashfree_env === "production" ? "production" : "sandbox" });
        const result = await cashfree.subscriptionsCheckout({
          subsSessionId: r.data.subscription_session_id,
          redirectTarget: "_self",
        });
        if (result?.error) throw new Error(result.error.message);
      }
    } catch (e) {
      toast.error(fmtErr(e));
      setBusy(false);
    }
  };

  const redeemLicense = async () => {
    if (!licenseKey.trim()) return toast.error("Enter your offline license key");
    setLicenseBusy(true);
    try {
      const response = await API.post("/billing/redeem-license", { license_key: licenseKey });
      toast.success(`${response.data.plan_name} plan activated until ${new Date(response.data.expires_at).toLocaleDateString("en-IN")}`);
      setLicenseKey("");
      await loadData();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setLicenseBusy(false);
    }
  };

  if (loading)
    return (
      <div className="flex h-64 items-center justify-center" data-testid="billing-loading">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );

  const currentKey = sub?.plan?.key;
  const usage = sub?.usage;

  return (
    <div className="space-y-8" data-testid="billing-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Subscription</h1>
        <p className="text-sm text-muted-foreground">Manage your plan, usage and billing</p>
      </div>

      {sub && (
        <div className="flex flex-wrap items-center justify-between gap-4 rounded-lg border bg-card p-5" data-testid="current-subscription">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10">
              <Crown size={20} className="text-primary" />
            </div>
            <div>
              <p className="font-heading font-bold" data-testid="current-plan-name">
                {sub.plan.name} Plan
                {sub.subscription.status === "expired" && (
                  <Badge variant="outline" className="ml-2 border-[#E02424]/30 bg-[#E02424]/10 text-[#E02424]">Expired</Badge>
                )}
              </p>
              <p className="text-xs text-muted-foreground">
                {sub.subscription.expires_at
                  ? `Renews/expires ${new Date(sub.subscription.expires_at).toLocaleDateString("en-IN")}`
                  : "No expiry"}
              </p>
            </div>
          </div>
          <div className="text-right" data-testid="usage-meter">
            <p className="text-sm font-medium tabular-nums">
              {usage.invoices_used} / {usage.invoice_limit === 0 ? "∞" : usage.invoice_limit} invoices this month
            </p>
            {usage.invoice_limit > 0 && (
              <div className="mt-1 h-1.5 w-48 overflow-hidden rounded-full bg-muted">
                <div
                  className={`h-full transition-all ${usage.invoices_used / usage.invoice_limit > 0.85 ? "bg-[#E02424]" : "bg-[#008A52]"}`}
                  style={{ width: `${Math.min(100, (usage.invoices_used / usage.invoice_limit) * 100)}%` }}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {!config?.payments_enabled && (
        <p className="rounded-md border border-[#B7791F]/30 bg-[#B7791F]/10 px-4 py-3 text-sm text-[#B7791F]" data-testid="payments-disabled-note">
          Online payments are being set up. To upgrade now, contact support and an admin can assign your plan manually.
        </p>
      )}

      <section className="max-w-xl space-y-3 rounded-lg border bg-card p-5" data-testid="offline-license-redemption">
        <div>
          <h2 className="font-heading text-lg font-semibold tracking-tight">Redeem Offline License</h2>
          <p className="text-xs text-muted-foreground">Enter the license key provided by the administrator to activate your subscription.</p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Input
            value={licenseKey}
            onChange={(e) => setLicenseKey(e.target.value)}
            placeholder="CBL-..."
            autoComplete="off"
            data-testid="offline-license-input"
          />
          <Button onClick={redeemLicense} disabled={licenseBusy || !licenseKey.trim()} data-testid="offline-license-redeem-button">
            {licenseBusy ? "Activating…" : "Activate"}
          </Button>
        </div>
      </section>

      <div className="flex justify-center">
        <Tabs value={period} onValueChange={setPeriod}>
          <TabsList data-testid="period-tabs">
            <TabsTrigger value="monthly" data-testid="monthly-tab">Monthly</TabsTrigger>
            <TabsTrigger value="yearly" data-testid="yearly-tab">Yearly (2 months free)</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {plans.map((p) => {
          const price = period === "yearly" ? p.price_yearly : p.price_monthly;
          const isCurrent = currentKey === p.key;
          return (
            <div
              key={p.key}
              className={`flex flex-col rounded-lg border bg-card p-6 transition-transform duration-200 hover:-translate-y-1 hover:shadow-md ${p.key === "pro" ? "border-primary ring-1 ring-primary" : ""}`}
              data-testid={`plan-card-${p.key}`}
            >
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-lg font-bold">{p.name}</h2>
                {isCurrent && <Badge className="bg-primary" data-testid={`current-badge-${p.key}`}>Current</Badge>}
              </div>
              <p className="mt-3 font-heading text-3xl font-bold tabular-nums">
                {price === 0 ? "Free" : inr(price)}
                {price > 0 && <span className="text-sm font-normal text-muted-foreground"> /{period === "yearly" ? "year" : "month"}</span>}
              </p>
              {price > 0 && (
                <p className="mt-0.5 text-xs text-muted-foreground" data-testid={`plan-gst-note-${p.key}`}>
                  + 18% GST extra ({inr(Math.round(price * 118) / 100)} incl. GST)
                </p>
              )}
              <ul className="mt-4 flex-1 space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <Check size={15} className="text-[#008A52]" />
                  {p.invoice_limit === 0 ? "Unlimited" : p.invoice_limit} invoices per month
                </li>
                <li className="flex items-center gap-2">
                  <Check size={15} className="text-[#008A52]" /> GST reports (GSTR-1, GSTR-3B) &amp; P&amp;L
                </li>
                <li className="flex items-center gap-2">
                  <Check size={15} className={p.features?.statement_import ? "text-[#008A52]" : "text-muted-foreground/40"} />
                  <span className={p.features?.statement_import ? "" : "text-muted-foreground line-through"}>Bank statement import</span>
                </li>
                <li className="flex items-center gap-2" data-testid={`plan-branch-limit-${p.key}`}>
                  <Check size={15} className={Number(p.features?.branch_limit || 0) > 0 ? "text-[#008A52]" : "text-muted-foreground/40"} />
                  <span className={Number(p.features?.branch_limit || 0) > 0 ? "" : "text-muted-foreground"}>
                    {Number(p.features?.branch_limit || 0)} additional {Number(p.features?.branch_limit || 0) === 1 ? "branch" : "branches"}
                  </span>
                </li>
                <li className="flex items-center gap-2" data-testid={`plan-invoice-email-${p.key}`}>
                  <Check size={15} className={(p.features?.invoice_email ?? true) ? "text-[#008A52]" : "text-muted-foreground/40"} />
                  <span className={(p.features?.invoice_email ?? true) ? "" : "text-muted-foreground line-through"}>Email invoices to customers</span>
                </li>
                <li className="flex items-center gap-2" data-testid={`plan-custom-sender-${p.key}`}>
                  <Check size={15} className={p.features?.custom_invoice_sender ? "text-[#008A52]" : "text-muted-foreground/40"} />
                  <span className={p.features?.custom_invoice_sender ? "" : "text-muted-foreground line-through"}>Use your own sender email</span>
                </li>
              </ul>
              <Button
                className="mt-6"
                variant={isCurrent || p.price_monthly === 0 ? "outline" : "default"}
                disabled={isCurrent || p.price_monthly === 0 || !config?.payments_enabled}
                onClick={() => setCheckoutPlan(p)}
                data-testid={`subscribe-${p.key}-button`}
              >
                {isCurrent ? "Current Plan" : p.price_monthly === 0 ? "Free" : `Subscribe to ${p.name}`}
              </Button>
            </div>
          );
        })}
      </div>

      {invoices.length > 0 && (
        <div className="space-y-3" data-testid="billing-history">
          <div>
            <h2 className="font-heading text-lg font-semibold tracking-tight">Billing History</h2>
            <p className="text-xs text-muted-foreground">GST tax invoices for your subscription payments (also emailed to you).</p>
          </div>
          <div className="overflow-x-auto rounded-lg border bg-card">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-2.5">Invoice No.</th>
                  <th className="px-4 py-2.5">Date</th>
                  <th className="px-4 py-2.5">Plan</th>
                  <th className="px-4 py-2.5 text-right">Total (incl. GST)</th>
                  <th className="px-4 py-2.5 text-right">PDF</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} className="border-b last:border-0" data-testid={`sub-invoice-row-${inv.number}`}>
                    <td className="px-4 py-2.5 font-medium">{inv.number}</td>
                    <td className="px-4 py-2.5">{inv.date}</td>
                    <td className="px-4 py-2.5 capitalize">{inv.plan_name} ({inv.period})</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{inr(inv.total)}</td>
                    <td className="px-4 py-2.5 text-right">
                      <Button variant="outline" size="sm" asChild data-testid={`sub-invoice-download-${inv.number}`}>
                        <a href={`${BACKEND_URL}/api/billing/invoices/${inv.id}/pdf`} download={`${inv.number}.pdf`}>Download</a>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={!!checkoutPlan} onOpenChange={(v) => !v && setCheckoutPlan(null)}>
        <DialogContent className="max-w-sm" data-testid="checkout-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">
              Subscribe to {checkoutPlan?.name} ({period})
            </DialogTitle>
            <DialogDescription className="sr-only">Continue to secure subscription checkout</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5 rounded-md border bg-muted/40 p-3 text-sm" data-testid="checkout-breakdown">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Plan ({period})</span>
                <span className="tabular-nums">{checkoutPlan && inr(period === "yearly" ? checkoutPlan.price_yearly : checkoutPlan.price_monthly)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">GST (18%)</span>
                <span className="tabular-nums">{checkoutPlan && inr(Math.round((period === "yearly" ? checkoutPlan.price_yearly : checkoutPlan.price_monthly) * 18) / 100)}</span>
              </div>
              <div className="flex justify-between border-t pt-1.5 font-semibold">
                <span>Total payable</span>
                <span className="tabular-nums">{checkoutPlan && inr(Math.round((period === "yearly" ? checkoutPlan.price_yearly : checkoutPlan.price_monthly) * 118) / 100)}</span>
              </div>
              <p className="pt-1 text-xs text-muted-foreground">
                Auto-renewal via {config?.gateway === "razorpay" ? "Razorpay" : "Cashfree"}, based on your selected payment method.
              </p>
            </div>
            {config?.gateway === "cashfree" && (
              <div className="space-y-2">
                <Label htmlFor="checkout-phone">Mobile Number</Label>
                <Input id="checkout-phone" data-testid="checkout-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="10-digit mobile" maxLength={10} />
              </div>
            )}
            <Button className="w-full" onClick={subscribe} disabled={busy} data-testid="checkout-pay-button">
              {busy ? "Opening checkout…" : "Continue to Payment"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
